import { BASE_INPUT_STYLE } from 'app/components/forms/Input';
import { getLinkStyles } from 'app/components/presentation/link/styles';
import { BLUE_A, DISABLED_LINK_COLOR, PURE_WHITE } from 'app/constants/Colors';
import { useClasses, IStyles } from 'app/utils/css';
import React, { ButtonHTMLAttributes } from 'react';
import BaseButton from 'app/components/common/BaseButton';

export enum ButtonVariant {
  AddExercise = 'smallWhite',
  Attention   = 'attention',
  Button      = 'button',
  Danger      = 'danger',
  Icon        = 'icon',
  IconSmall   = 'iconSmall',
  Link        = 'link',
  Modal       = 'modal',
  ModalFooter = 'modalFooter',
  Small       = 'small',
  Tiny        = 'tiny',
}

// Deprecated, prefer ButtonVariant.
export const BUTTON       = ButtonVariant.Button;
export const LINK         = ButtonVariant.Link;
export const SMALL        = ButtonVariant.Small;
export const ADD_EXERCISE = ButtonVariant.AddExercise;
export const MODAL        = ButtonVariant.Modal;
export const MODAL_FOOTER = ButtonVariant.ModalFooter;
export const ATTENTION    = ButtonVariant.Attention;
export const DANGER       = ButtonVariant.Danger;
export const TINY         = ButtonVariant.Tiny;
export const ICON         = ButtonVariant.Icon;
export const ICON_SMALL   = ButtonVariant.IconSmall;

export interface IProps extends Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'disabled' | 'onClick' | 'onMouseDown' | 'type' | 'title' | 'aria-label'
> {
  variant?: ButtonVariant;
  buttonRef?: any;
  showOutline?: boolean;
  'data-testid'?: string;
}

const Button = ({
  type = 'button',
  disabled = false,
  onClick,
  onMouseDown,
  variant = ButtonVariant.Button,
  children,
  buttonRef,
  showOutline = true,
  title,
  ...props
}: IProps) => {
  const classes = useClasses({
    button: (
      variant === ButtonVariant.Link ? [
        {
          ...getLinkStyles(),
          position: 'relative',
          border: '0',
          background: '0',
          padding: '0',
        },

        disabled && {
          cursor: 'default',
          color: DISABLED_LINK_COLOR,
        },
      ] : [
        variant === ButtonVariant.Button && {
          ...BUTTON_STYLE,
          ...ACTIVE_EFFECT,
        },

        variant === ButtonVariant.Attention && {
          ...BUTTON_STYLE,
          ...ACTIVE_EFFECT,
          ...ATTENTION_BUTTON_STYLE,
        },

        variant === ButtonVariant.Small && {
          ...BUTTON_STYLE,
          ...ACTIVE_EFFECT,
          ...SMALL_BUTTON,
        },

        variant === ButtonVariant.AddExercise && {
          ...BUTTON_STYLE,
          ...ACTIVE_EFFECT,
          ...SMALL_BUTTON,
          backgroundColor: '#fff',
        },

        variant === ButtonVariant.Danger && {
          ...BUTTON_STYLE,
          ...ACTIVE_EFFECT,
          ...DANGER_BUTTON_STYLE,
        },

        variant === ButtonVariant.Modal && {
          ...BUTTON_STYLE,
          backgroundColor: BLUE_A,
          border: 'none',
          boxShadow: 'none',
          color: PURE_WHITE,
          padding: '0.2em 0.4em',
        },

        variant === ButtonVariant.ModalFooter && MODAL_FOOTER_STYLE,
        variant === ButtonVariant.Tiny && TINY_BUTTON_STYLE,
        variant === ButtonVariant.Icon && ICON_BUTTON_STYLE,

        variant === ButtonVariant.IconSmall && {
          ...ICON_BUTTON_STYLE,
          // When we reduce the font size, we also have to increase the width/height/line-height
          // accordingly because they are relative to the font size.
          fontSize: '0.8em',
          width: '1.25em',
          height: '1.25em',
          lineHeight: '1.25em',
        },

        disabled && {
          ...BUTTON_DISABLED_STYLE,

          ':active': {
            ...BUTTON_DISABLED_STYLE,
            // Override browser native styles
            transform: 'none',
            borderStyle: 'solid',
          },
        },
      ]
    ),
  });

  return (
    <BaseButton
      className={classes.button}
      type={type}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={onMouseDown}
      getRef={buttonRef}
      title={title}
      aria-label={props['aria-label']}
      data-testid={props['data-testid']}
    >
      {children}
    </BaseButton>
  );
};

const BUTTON_DISABLED_STYLE: IStyles = {
  cursor: 'default',
  boxShadow: 'none',
  opacity: 0.5,
};

export const BUTTON_STYLE: IStyles = {
  ...BASE_INPUT_STYLE,
  cursor: 'pointer',
  position: 'relative',
  backgroundColor: '#eee',
  padding: '5px 10px',
  boxShadow: '0px 1px 2px rgba(128,128,128,0.5)',
  flexGrow: 0,
  flexShrink: 0,

  ':disabled': BUTTON_DISABLED_STYLE,
};

export const ACTIVE_EFFECT: IStyles = {
  ':active': {
    transform: 'translateY(1px)',
    boxShadow: '0px 0px 1px rgba(128,128,128,0.5)',
  },
};

export const ATTENTION_BUTTON_STYLE: IStyles = {
  color: '#fff',
  backgroundColor: '#ff8b00',
  border: '0',
};

export const DANGER_BUTTON_STYLE: IStyles = {
  color: '#fff',
  backgroundColor: 'red',
  border: '0',
};

const SMALL_BUTTON: IStyles = {
  fontSize: '12px',
  padding: '4px 7px',
};

export const MODAL_FOOTER_STYLE: IStyles = {
  ...BUTTON_STYLE,
  ...ACTIVE_EFFECT,
  backgroundColor: '#fff',
};

export const TINY_BUTTON_STYLE: IStyles = {
  ...BUTTON_STYLE,
  ...ACTIVE_EFFECT,
  ...SMALL_BUTTON,
  fontSize: '10px',
  padding: '2px 4px',
};

export const ICON_BUTTON_STYLE: IStyles = {
  ...BUTTON_STYLE,
  ...ACTIVE_EFFECT,
  display: 'block',
  boxSizing: 'content-box',
  padding: '5px',
  width: '1em',
  height: '1em',
  lineHeight: '1em',
  textAlign: 'center',
};

export default Button;
